import { useState, useEffect } from "react";
import { useGetMyProfile, useUpdateMyProfile } from "@workspace/api-client-react";
import { getGetMyProfileQueryKey } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { UserCircle, CheckCircle, Link as LinkIcon, Clock } from "lucide-react";
import { Link } from "wouter";

export default function ProfilePage() {
  const { data: profile, isLoading } = useGetMyProfile();
  const updateProfile = useUpdateMyProfile();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName ?? "");
    }
  }, [profile]);

  const handleSave = () => {
    updateProfile.mutate({
      data: { displayName: displayName || null }
    }, {
      onSuccess: () => {
        setSavedMsg("已儲存更改");
        queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() });
        setTimeout(() => setSavedMsg(""), 3000);
      }
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-xl">
        <Skeleton className="h-8 w-40 mb-6" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-secondary/30 py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-xl">
          <h1 className="text-2xl font-bold mb-1">個人資料</h1>
          <p className="text-muted-foreground text-sm">更新您的帳戶資料</p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-xl space-y-6">
        {/* Account info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-primary" />
              帳戶資料
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium cursor-pointer">電郵地址</label>
              <Input value={profile?.email ?? ""} disabled className="bg-secondary/50" data-testid="input-email" />
              <p className="text-xs text-muted-foreground">電郵地址不可更改</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium cursor-pointer">顯示名稱</label>
              <Input
                placeholder="輸入您的名稱"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                data-testid="input-display-name"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={handleSave} disabled={updateProfile.isPending} data-testid="button-save-profile">
                {updateProfile.isPending ? "儲存中..." : "儲存更改"}
              </Button>
              {savedMsg && (
                <span className="flex items-center gap-1.5 text-sm text-primary">
                  <CheckCircle className="h-4 w-4" />
                  {savedMsg}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Telegram info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <img src="/telegram-logo.webp" alt="Telegram" width={20} height={20} />
              Telegram 綁定
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {profile?.telegramId ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">已綁定 Telegram</p>
                    <p className="text-xs text-muted-foreground">
                      {profile.telegramUsername ? profile.telegramUsername : `Telegram ID: ${profile.telegramId}`}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-auto bg-primary/10 text-primary border-primary/20">
                    已連結
                  </Badge>
                </div>
                {profile.membershipTier === "paid" && (
                  <p className="text-xs text-muted-foreground">
                    如需重新綁定，請前往
                    <Link href="/dashboard" className="text-primary underline underline-offset-2 ml-1">控制台</Link>
                    操作。
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">尚未綁定 Telegram 帳號。</p>
                {profile?.membershipTier === "paid" ? (
                  <div className="flex items-center gap-2">
                    <Link href="/dashboard">
                      <Button size="sm" variant="outline" className="gap-1.5">
                        <LinkIcon className="h-3.5 w-3.5" />
                        前往控制台綁定
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">升級付費會員後可綁定 Telegram，加入付費會員頻道。</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* WhatsApp info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <img src="/whatsapp-logo.webp" alt="WhatsApp" width={20} height={20} />
              WhatsApp 綁定
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {profile?.whatsappRequestStatus === "confirmed" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">已綁定 WhatsApp</p>
                    <p className="text-xs text-muted-foreground">
                      {profile.whatsappUsername ? `@${profile.whatsappUsername}` : profile.whatsappPhone}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-auto bg-primary/10 text-primary border-primary/20">
                    已連結
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  如需修改電話號碼，請前往
                  <Link href="/dashboard" className="text-primary underline underline-offset-2 ml-1">控制台</Link>
                  操作。
                </p>
              </div>
            ) : profile?.whatsappRequestStatus === "pending" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <Clock className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">審核中</p>
                    <p className="text-xs text-muted-foreground">
                      {profile.whatsappUsername ? `@${profile.whatsappUsername}` : profile.whatsappPhone}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-auto bg-amber-50 text-amber-600 border-amber-300">
                    待確認
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">我們確認後會將你加入付費會員 WhatsApp 社群。</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">尚未綁定 WhatsApp 電話號碼。</p>
                {profile?.membershipTier === "paid" ? (
                  <div className="flex items-center gap-2">
                    <Link href="/dashboard">
                      <Button size="sm" variant="outline" className="gap-1.5">
                        <LinkIcon className="h-3.5 w-3.5" />
                        前往控制台綁定
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">升級付費會員後可綁定 WhatsApp，加入付費會員社群。</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
